import React, { useState, useEffect, useRef } from 'react';
import {
  Film, AudioLines, ImageIcon,
  Check, ExternalLink, Box, X
} from 'lucide-react';
import { API_BASE_URL } from '../constants';
import { getToken } from '../api';
import QuickTooltip from '../components/QuickTooltip';
import localCacheService from '../services/localCacheService';

// --- 常量 ---
export const TOOLBAR_BG = 'var(--toolbar-bg)';
export const PRIMARY_COLOR = '#3b82f6';

/** 格式化字节数为可读字符串 */
export function formatBytes(bytes) {
  if (!bytes || bytes <= 0) return '0B';
  if (bytes < 1024) return bytes + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + 'KB';
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
}

/** 紧凑体积（用于节点角标）：≥1MB 显示 32.1M / 8M；<1MB 显示 KB */
export function formatBytesCompact(bytes) {
  if (!bytes || bytes <= 0) return '0';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return Math.max(1, Math.round(bytes / 1024)) + 'K';
  // 个位数 MB 保留 1 位小数，两位数以上取整，读起来更干净
  return (mb < 10 ? mb.toFixed(1) : Math.round(mb)) + 'M';
}

/**
 * 读取媒体文件体积（字节）的通用 Hook。
 * - blob:/data: URL → 直接读 blob.size（本地，几乎零成本）
 * - http(s) URL → 先发 HEAD 取 Content-Length（不下载正文，对大视频友好）；
 *                 HEAD 不可用时回退 GET range 仅取头部
 * 拿不到返回 0（调用方据此决定不显示体积框）。
 */
export function useFileBytes(url) {
  const [bytes, setBytes] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!url) { setBytes(0); return; }
    (async () => {
      try {
        if (url.startsWith('blob:') || url.startsWith('data:')) {
          const blob = await (await fetch(url)).blob();
          if (!cancelled) setBytes(blob.size);
          return;
        }
        // http(s)：HEAD 拿 Content-Length，避免下载整段视频
        const head = await fetch(url, { method: 'HEAD' });
        const len = head.headers.get('content-length');
        if (len && Number(len) > 0) {
          if (!cancelled) setBytes(Number(len));
          return;
        }
        if (!cancelled) setBytes(0); // 没有 Content-Length 就不显示
      } catch (e) {
        if (!cancelled) setBytes(0);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);
  return bytes;
}

/**
 * 通用工具栏按钮组件 (带胶囊 Tooltip)
 */
export const ToolBtn = ({ onClick, title, active, disabled, children, color, size = '28px' }) => {
  const [hover, setHover] = useState(false);
  return (
    <QuickTooltip content={title} position="top" delay={300}>
      <div 
        onMouseDown={(e) => {
          // 阻止默认的鼠标按下行为，防止 textarea 失焦
          // 这对于中文输入法候选框的正确定位至关重要
          e.preventDefault();
        }}
        onClick={(e) => { e.stopPropagation(); if (!disabled && onClick) onClick(e); }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: size, height: size, borderRadius: '6px',
          cursor: disabled ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: !disabled && (hover || active) ? 'var(--bg-hover)' : 'transparent',
          color: disabled ? 'var(--text-muted)' : (hover || active ? 'var(--text-hover)' : (color || 'var(--node-toolbar-btn)')),
          opacity: disabled ? 0.4 : 1,
          transition: 'all 0.1s'
        }}
      >
        {children}
      </div>
    </QuickTooltip>
  );
};

// 从 URL 中提取真实文件扩展名
export const getFileExtFromUrl = (url, fallback = '') => {
  if (!url) return fallback;
  try {
    // 去掉 query 和 hash，取路径部分
    const pathname = new URL(url, 'http://localhost').pathname;
    const lastDot = pathname.lastIndexOf('.');
    if (lastDot > 0) {
      const ext = pathname.substring(lastDot).toLowerCase();
      // 只接受常见媒体扩展名
      const validExts = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.bmp', '.tiff', '.svg',
                         '.mp4', '.mov', '.avi', '.webm', '.mkv', '.flv',
                         '.mp3', '.wav', '.ogg', '.aac', '.flac', '.m4a'];
      if (validExts.includes(ext)) return ext;
    }
  } catch (e) {}
  return fallback;
};

// 智能构造下载文件名（避免扩展名重复）
export const buildDownloadFilename = (title, url, defaultExt) => {
  if (!title) return 'download' + defaultExt;
  
  const ext = getFileExtFromUrl(url, defaultExt);
  
  // 如果 title 已经以这个扩展名结尾，直接返回
  if (title.toLowerCase().endsWith(ext.toLowerCase())) {
    return title;
  }
  
  // 否则，去掉 title 中现有的扩展名（如果有），再加上正确的扩展名
  const lastDot = title.lastIndexOf('.');
  if (lastDot > 0) {
    const titleWithoutExt = title.substring(0, lastDot);
    return titleWithoutExt + ext;
  }
  
  return title + ext;
};

// 下载进度浮层 — 纯 DOM，不依赖 React
const showDownloadToast = (filename) => {
  const el = document.createElement('div');
  el.id = 'dl-toast-' + Date.now();
  Object.assign(el.style, {
    position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%) translateY(0)',
    background: 'rgba(30,30,30,0.92)', backdropFilter: 'blur(10px)',
    color: '#e2e8f0', fontSize: '13px', fontWeight: '500',
    padding: '10px 20px', borderRadius: '10px',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    zIndex: 999999, display: 'flex', alignItems: 'center', gap: '10px',
    transition: 'opacity 0.3s ease, transform 0.3s ease',
    opacity: '0', pointerEvents: 'none'
  });
  const shortName = filename && filename.length > 20 ? filename.slice(0, 17) + '...' : (filename || '文件');
  el.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="animation:spin 1s linear infinite;flex-shrink:0"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg><span>正在下载 ${shortName}</span>`;
  document.body.appendChild(el);
  requestAnimationFrame(() => { el.style.opacity = '1'; });
  return {
    done: () => {
      el.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22c55e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg><span>下载完成</span>`;
      setTimeout(() => { el.style.opacity = '0'; el.style.transform = 'translateX(-50%) translateY(8px)'; setTimeout(() => el.remove(), 350); }, 1200);
    },
    fail: () => {
      el.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg><span>下载失败</span>`;
      setTimeout(() => { el.style.opacity = '0'; setTimeout(() => el.remove(), 350); }, 2000);
    }
  };
};

// 代理下载函数（解决跨域文件无法直接下载的问题）
export const downloadFile = async (url, filename) => {
  if (!url) return;

  // 🔧 blob: / data: 链接是浏览器本地内存，无需经过后端代理，直接前端下载
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('本地文件下载失败:', err);
    }
    return;
  }
  
  // 远程文件：通过后端代理下载，使用 fetch + blob 方式携带 JWT token
  const proxyUrl = `${API_BASE_URL}/api/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename || 'download')}`;
  const toast = showDownloadToast(filename);
  
  try {
    const token = getToken();
    const resp = await fetch(proxyUrl, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    });
    if (!resp.ok) throw new Error(`下载失败: ${resp.status}`);
    
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    // 从 Content-Disposition 或 filename 参数取文件名
    const disposition = resp.headers.get('Content-Disposition');
    const match = disposition && disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
    a.download = match ? decodeURIComponent(match[1]) : (filename || 'download');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    toast.done();
  } catch (err) {
    console.error('下载失败:', err);
    // 兜底：用原始 URL 直接下载（不走代理），避免弹出未登录页面
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      toast.done();
    } catch (e) {
      console.error('兜底下载也失败:', e);
      toast.fail();
    }
  }
};

// --- SVG 图标 (箭头不扎线版) ---
export const ArrowHeadLeft = ({ x, y }) => <path d={`M${x+4} ${y-4}L${x} ${y}L${x+4} ${y+4}`} />;
export const ArrowHeadRight = ({ x, y }) => <path d={`M${x-4} ${y-4}L${x} ${y}L${x-4} ${y+4}`} />;
export const IconFirstFrame = ({ size=16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="4" x2="6" y2="20" strokeDasharray="4 2" /><line x1="18" y1="12" x2="10" y2="12" /><ArrowHeadLeft x={10} y={12} /></svg>);
export const IconMiddleFrame = ({ size=16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="4" x2="12" y2="20" strokeDasharray="4 2" /><line x1="4" y1="12" x2="9" y2="12" /><ArrowHeadRight x={9} y={12} /><line x1="20" y1="12" x2="15" y2="12" /><ArrowHeadLeft x={15} y={12} /></svg>);
export const IconLastFrame = ({ size=16 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="4" x2="18" y2="20" strokeDasharray="4 2" /><line x1="6" y1="12" x2="14" y2="12" /><ArrowHeadRight x={14} y={12} /></svg>);

// --- 样式对象 ---
export const styles = {
  // 【优化】间距改紧凑 (gap: 2px)，对齐调整
  titleWrapper: (color, zoom, nodeWidth) => ({ 
    position: 'absolute', 
    top: '-20px', 
    left: '0px', 
    display: 'flex', 
    alignItems: 'center', 
    gap: '2px', // 缩小图标与文字间距
    color, 
    transform: `scale(${1 / zoom})`, 
    transformOrigin: 'bottom left', 
    width: `${nodeWidth * zoom}px`, 
    pointerEvents: 'none' 
  }),
  // 【优化】取消固定宽高，让图标紧凑贴边
  iconBadge: (type) => { 
    let color = '#ccc'; 
    if (type === 'image') color = '#3b82f6'; 
    if (type === 'video') color = '#fbbf24'; 
    if (type === 'audio') color = '#f97316'; 
    if (type === 'text') color = '#22c55e'; 
    return { 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      color, 
      minWidth: '14px',
      margin: 0,
      padding: 0
    }; 
  },
  // 【优化】字体稍微加粗回 400
  nodeTitleInput: { 
    background: 'none', 
    border: 'none', 
    color: 'inherit', 
    fontSize: '14px', 
    outline: 'none', 
    fontWeight: '400', 
    flex: 1, 
    minWidth: 0, 
    padding: 0, // 去除内间距
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    pointerEvents: 'auto',
    fontFamily: 'system-ui, sans-serif'
  },
  deleteBtn: { position: 'absolute', top: '6px', right: '6px', width: '18px', height: '18px', background: 'rgba(0, 0, 0, 0.2)', backdropFilter: 'blur(4px)', border: '0.5px solid rgba(255, 255, 255, 0.1)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 100 },
  handleLeft: { width: '24px', height: '24px', background: '#222222', border: '1px solid #ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', zIndex: 99999, top: '50%', transform: 'translateY(-50%)', position: 'absolute', left: '-32px', pointerEvents: 'all' },
  handleRight: { width: '24px', height: '24px', background: '#222222', border: '1px solid #ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', zIndex: 99999, top: '50%', transform: 'translateY(-50%)', position: 'absolute', right: '-32px', pointerEvents: 'all' },
  resizeHandle: { position: 'absolute', right: '4px', bottom: '4px', width: '12px', height: '12px', cursor: 'nwse-resize', borderRight: '2px solid rgba(80,80,80,0.65)', borderBottom: '2px solid rgba(80,80,80,0.65)', zIndex: 105 },
  mediaFull: { width: '100%', height: '100%', objectFit: 'contain', borderRadius: '13px' },
  // 【新增】统一的文本节点样式
  textNodeArea: {
    width: '100%', height: '100%',
    background: 'transparent', border: 'none',
    color: 'var(--text-node-fg)', fontSize: '15px', lineHeight: '1.6',
    resize: 'none', padding: '16px', outline: 'none',
    fontFamily: 'inherit', fontWeight: '500',
    display: 'block'
  },
  // 【新增】错误提示层样式
  errorOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(239, 68, 68, 0.1)', // 红色半透明背景
    border: '1px solid #ef4444',
    borderRadius: '13px', // 匹配节点圆角
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '12px',
    zIndex: 10
  },
  errorText: {
    color: '#f87171', fontSize: '13px', fontWeight: 500, textAlign: 'center',
    backgroundColor: 'rgba(0,0,0,0.9)', padding: '6px 14px', borderRadius: '20px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
    border: '1px solid rgba(239, 68, 68, 0.3)'
  },
  stackFrame: { position: 'absolute', top: '8px', left: '8px', width: '100%', height: '100%', border: '0.5px solid rgba(255,255,255,0.3)', background: '#222222', borderRadius: '14px', zIndex: -1, transform: 'rotate(-2deg)' },
  stackBadge: { position: 'absolute', top: '6px', right: '28px', height: '16px', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', border: '0.5px solid rgba(255,255,255,0.2)', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '1px', padding: '0 3px', color: '#fff', fontSize: '10px', fontWeight: 'bold', cursor: 'pointer', zIndex: 110 },
  
  // Toolbar styles
  toolbar: { position: 'absolute', top: 'calc(100% + 5px)', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center', gap: '8px', background: TOOLBAR_BG, border: '1px solid var(--border-strong)', padding: '6px 14px', borderRadius: '20px', boxShadow: '0 4px 15px rgba(0,0,0,0.4)', pointerEvents: 'auto', zIndex: 200, height: '32px' },
  toolBtn: { color: 'var(--node-toolbar-btn)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', padding: '4px', borderRadius: '4px' },
  toolDivider: { width: '1px', height: '14px', background: 'var(--border-strong)' },

};

// --- 分辨率常量和工具函数 ---
export const RESOLUTION_LEVELS = {
  HI: { text: 'HI', color: '#ef4444', minPixels: 5120 * 2880 },
  '4K': { text: '4K', color: '#f97316', minPixels: 3840 * 2160 },
  HD: { text: 'HD', color: '#eab308', minPixels: 1920 * 1080 },
  SD: { text: 'SD', color: '#22c55e', minPixels: 1280 * 720 },
  LD: { text: 'LD', color: '#9ca3af', minPixels: 0 }
};

// 目标像素略高于阈值，避免 round 误差导致降级后被误判为更低级别
export const DOWNSCALE_TARGETS = {
  '4K': 3840 * 2160 * 1.02,  // 略高于 4K 阈值
  'HD': 1920 * 1080 * 1.02,  // 略高于 HD 阈值
  'SD': 1280 * 720 * 1.02    // 略高于 SD 阈值
};

// 获取当前分辨率级别
export const getResolutionLevel = (w, h) => {
  if (!w || !h) return null;
  const pixels = w * h;
  if (pixels >= RESOLUTION_LEVELS.HI.minPixels) return RESOLUTION_LEVELS.HI;
  if (pixels >= RESOLUTION_LEVELS['4K'].minPixels) return RESOLUTION_LEVELS['4K'];
  if (pixels >= RESOLUTION_LEVELS.HD.minPixels) return RESOLUTION_LEVELS.HD;
  if (pixels >= RESOLUTION_LEVELS.SD.minPixels) return RESOLUTION_LEVELS.SD;
  return RESOLUTION_LEVELS.LD;
};

// 获取可降级的选项列表
export const getDownscaleOptions = (w, h) => {
  if (!w || !h) return [];
  const pixels = w * h;
  const options = [];
  
  // 只返回低于当前像素数的级别（且不包括 LD）
  if (pixels > DOWNSCALE_TARGETS['4K']) {
    options.push({ key: '4K', ...RESOLUTION_LEVELS['4K'], targetPixels: DOWNSCALE_TARGETS['4K'] });
  }
  if (pixels > DOWNSCALE_TARGETS['HD']) {
    options.push({ key: 'HD', ...RESOLUTION_LEVELS.HD, targetPixels: DOWNSCALE_TARGETS['HD'] });
  }
  if (pixels > DOWNSCALE_TARGETS['SD']) {
    options.push({ key: 'SD', ...RESOLUTION_LEVELS.SD, targetPixels: DOWNSCALE_TARGETS['SD'] });
  }
  
  return options;
};

// 计算降级后的尺寸（保持宽高比）
export const calculateDownscaleSize = (w, h, targetPixels) => {
  const ratio = w / h;
  const newW = Math.round(Math.sqrt(targetPixels * ratio));
  const newH = Math.round(newW / ratio);
  return { w: newW, h: newH };
};

// --- 压缩体积（保持分辨率，不改像素）---
// 固定档位（字节）：1M / 5M / 10M / 15M
const COMPRESS_LEVELS_MB = [15, 10, 5, 1];

// 根据当前体积返回可选压缩档（只保留比当前小的整数档，从大到小排列）
// 留 5% 余量：体积已接近某档时不再列该档，避免"压了几乎没变小"
export const getCompressTargets = (currentBytes) => {
  if (!currentBytes || currentBytes <= 0) return [];
  const MB = 1024 * 1024;
  return COMPRESS_LEVELS_MB
    .filter((mb) => currentBytes > mb * MB * 1.05)
    .map((mb) => ({ key: `${mb}M`, mb, targetBytes: mb * MB }));
};

// --- 通用组件 ---

// 格式化生成耗时
const formatGenerationTime = (seconds) => {
  if (!seconds || seconds <= 0) return null;
  if (seconds < 60) return `${seconds}秒`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}分${secs}秒` : `${mins}分`;
};

export const NodeResolutionTag = ({ w, h, generationTime, canDownscale, downscaleOptions, onDownscale, showMenu, onToggleMenu,
  fileBytes, compressTargets, onCompress, showCompressMenu, onToggleCompressMenu, compressing, uploading }) => {
  // 🆕 点击外部（画布空白、挪动画布等）自动收起下拉框。
  //    仅在某个菜单打开时挂监听，点到根节点外就调用对应 toggle 关闭。
  const tagRootRef = useRef(null);
  useEffect(() => {
    if (!showMenu && !showCompressMenu) return;
    const onDocMouseDown = (e) => {
      if (tagRootRef.current && tagRootRef.current.contains(e.target)) return;
      if (showMenu && onToggleMenu) onToggleMenu();
      if (showCompressMenu && onToggleCompressMenu) onToggleCompressMenu();
    };
    // capture 阶段，确保画布拖拽 handler 之前先收起
    window.addEventListener('mousedown', onDocMouseDown, true);
    return () => window.removeEventListener('mousedown', onDocMouseDown, true);
  }, [showMenu, showCompressMenu, onToggleMenu, onToggleCompressMenu]);

  if (!w || !h) return null;
  
  // 恢复原来的一致性逻辑 (参考 AnnotationModal/ImageCropperModal)
  const pixels = w * h;
  let level = { text: 'HI', color: '#ef4444' };
  
  if (pixels < 1280 * 720) level = { text: 'LD', color: '#9ca3af' };
  else if (pixels < 1920 * 1080) level = { text: 'SD', color: '#22c55e' }; // Green
  else if (pixels < 3840 * 2160) level = { text: 'HD', color: '#eab308' }; // Yellow
  else if (pixels < 5120 * 2880) level = { text: '4K', color: '#f97316' }; // Orange
  
  const timeText = formatGenerationTime(generationTime);
  const hasDownscaleFeature = canDownscale !== undefined; // 是否启用降分辨率功能
  
  return (
    <div ref={tagRootRef} className="node-res-tag" style={{ position: 'absolute', top: '6px', left: '6px', zIndex: 5 }}>
      <div 
        className={hasDownscaleFeature ? 'nodrag' : ''}
        title={uploading ? '上传完成后可用' : (hasDownscaleFeature && canDownscale ? '降分辨率' : undefined)}
        onClick={(e) => {
          if (hasDownscaleFeature && canDownscale && !uploading && onToggleMenu) {
            e.stopPropagation();
            onToggleMenu();
          }
        }}
        style={{
          background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.8)',
          fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
          pointerEvents: hasDownscaleFeature ? 'auto' : 'none', 
          backdropFilter: 'blur(2px)',
          display: 'flex', alignItems: 'center', gap: '4px', fontWeight: '500',
          whiteSpace: 'nowrap',
          cursor: (hasDownscaleFeature && canDownscale && !uploading) ? 'pointer' : 'default',
          transition: 'all 0.15s'
        }}
      >
        <span style={{ 
            color: level.color, 
            border: `1px solid ${level.color}`,
            borderRadius: '3px',
            padding: '0 3px',
            fontSize: '9px',
            fontWeight: '900',
            lineHeight: '1.2'
        }}>
          {level.text}
        </span>
        <span>{w} × {h}</span>
        {/* 生成耗时标签 */}
        {timeText && (
          <span style={{
            color: 'rgba(255,255,255,0.5)',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '3px',
            padding: '0 3px',
            fontSize: '9px',
            fontWeight: '500',
            lineHeight: '1.2',
            marginLeft: '2px'
          }}>
            {timeText}
          </span>
        )}
        {/* 体积框（常驻）：纯描边小巧样式，与左侧分辨率色块一致；点击在正下方展开压缩档位 */}
        {fileBytes > 0 && (
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <span
              className="nodrag"
              title={uploading ? '上传完成后可压缩' : (compressTargets && compressTargets.length > 0 ? '压缩体积' : '文件大小')}
              onClick={(e) => {
                if (!uploading && compressTargets && compressTargets.length > 0 && onToggleCompressMenu) {
                  e.stopPropagation();
                  onToggleCompressMenu();
                }
              }}
              style={{
                color: compressing ? '#60a5fa' : 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(255,255,255,0.35)',
                borderRadius: '3px',
                padding: '0 3px',
                fontSize: '9px',
                fontWeight: '900',
                lineHeight: '1.2',
                marginLeft: '2px',
                opacity: uploading ? 0.55 : 1,
                cursor: (!uploading && compressTargets && compressTargets.length > 0) ? 'pointer' : 'default',
                transition: 'all 0.15s'
              }}
            >
              {compressing ? '压缩中…' : formatBytesCompact(fileBytes)}
            </span>

            {/* 压缩档位下拉：嵌在体积框内，正下方对齐（保持分辨率，只压文件大小） */}
            {showCompressMenu && compressTargets && compressTargets.length > 0 && (
              <div
                className="nodrag"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '0px',
                  marginTop: '2px',
                  background: '#1e293b',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: 6,
                  padding: 4,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                  zIndex: 100
                }}
              >
                {compressTargets.map(t => (
                  <button
                    key={t.key}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onCompress) onCompress(t);
                    }}
                    style={{
                      padding: '1px 4px',
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,0.45)',
                      borderRadius: 3,
                      color: 'rgba(226,232,240,0.95)',
                      fontSize: 9,
                      fontWeight: 700,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      lineHeight: 1.2
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(96,165,250,0.25)'; e.currentTarget.style.borderColor = '#60a5fa'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                  >
                    {t.key}
                  </button>
                ))}
              </div>
            )}
          </span>
        )}
      </div>
      
      {/* 降分辨率下拉菜单 */}
      {hasDownscaleFeature && showMenu && canDownscale && downscaleOptions && downscaleOptions.length > 0 && (
        <div 
          className="nodrag"
          style={{
            position: 'absolute',
            top: '100%',
            left: '0px',
            marginTop: '2px',
            background: '#1e293b',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 8,
            padding: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            zIndex: 100
          }}
        >
          {downscaleOptions.map(opt => (
            <button
              key={opt.key}
              onClick={(e) => { 
                e.stopPropagation(); 
                if (onDownscale) onDownscale(opt); 
              }}
              style={{
                padding: '2px 4px',
                background: 'transparent',
                border: `1px solid ${opt.color}`,
                borderRadius: 3,
                color: opt.color,
                fontSize: 9,
                fontWeight: 900,
                cursor: 'pointer',
                transition: 'all 0.15s',
                lineHeight: 1.2
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${opt.color}22`; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {opt.text}
            </button>
          ))}
        </div>
      )}

    </div>
  );
};

export function NebulaBackground({ type, animated = true, opacity = 1 }) {
  const isVideo = type === 'video';

  const theme = isVideo 
    ? { c1: '#8f5e5e', c2: '#b08585' } 
    : { c1: '#4ba6ec', c2: '#5f748a' };

  const animStyle1 = animated 
    ? { animation: 'spin-organic-1 5s ease-in-out infinite alternate' }
    : { transform: 'rotate(0deg) scale(1)' }; // Static start frame
    
  const animStyle2 = animated
    ? { animation: 'spin-organic-2 7s ease-in-out infinite alternate-reverse' }
    : { transform: 'rotate(0deg)' }; // Static start frame

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity, zIndex: 0, overflow: 'hidden', pointerEvents: 'none', contentVisibility: 'auto' }}>
        {/* Layer 1: Main Organic Spin */}
        <div style={{
            position: 'absolute', width: '120%', height: '120%',
            background: `conic-gradient(from 0deg, transparent 0%, ${theme.c1} 40%, ${theme.c2} 50%, transparent 60%)`,
            borderRadius: '50%',
            filter: 'blur(20px)',
            mixBlendMode: 'screen',
            willChange: animated ? 'transform' : 'auto',
            ...animStyle1
        }} />
        {/* Layer 2: Secondary Randomizer */}
        <div style={{
            position: 'absolute', width: '100%', height: '100%',
            background: `conic-gradient(from 120deg, transparent 0%, ${theme.c2} 30%, transparent 60%)`,
            borderRadius: '50%',
            opacity: 0.5,
            filter: 'blur(20px)',
            mixBlendMode: 'screen',
            willChange: animated ? 'transform' : 'auto',
            ...animStyle2
        }} />
         <style>{`
        /* Exact keyframes from magic-loading.html */
        @keyframes spin-organic-1 {
            0%   { transform: rotate(0deg) scale(1); }
            40%  { transform: rotate(140deg) scale(1.1); }
            100% { transform: rotate(360deg) scale(0.95); }
        }
        @keyframes spin-organic-2 {
            0%   { transform: rotate(0deg); }
            100% { transform: rotate(-360deg); }
        }
      `}</style>
    </div>
  );
}

export const LoadingOverlay = ({ type, statusInfo, generationStartTime, modelName, modelLogo, transparent }) => {
  const isVideo = type === 'video';
  const isAudio = type === 'audio';
  
  // 本地计时：从 generationStartTime 自主计算 elapsed（跨设备/刷新后仍有效）
  const [localElapsed, setLocalElapsed] = useState(0);
  // 图片加载错误状态
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [modelLogo]);
  
  useEffect(() => {
    if (!generationStartTime) {
      setLocalElapsed(0);
      return;
    }
    // 立即计算一次
    setLocalElapsed(Math.round((Date.now() - generationStartTime) / 1000));
    // 每秒更新
    const timer = setInterval(() => {
      setLocalElapsed(Math.round((Date.now() - generationStartTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [generationStartTime]);
  
  // 格式化已等待时间
  const formatElapsed = (seconds) => {
    if (!seconds || seconds < 0) return null;
    if (seconds < 60) return `${seconds}秒`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}分${secs}秒`;
  };
  
  // 优先使用本地计算的 elapsed，fallback 到 statusInfo 中的
  const elapsed = localElapsed || statusInfo?.elapsed || 0;
  const elapsedText = elapsed > 0 ? formatElapsed(elapsed) : null;
  const statusText = statusInfo?.status || null;
  const progress = statusInfo?.progress || 0; // 获取进度（0-100，仅部分Provider支持）
  const retryText = statusInfo?.retryCount > 0 ? `重试 ${statusInfo.retryCount}/${statusInfo.maxRetries || 3}` : null;
  const isSavingResult = statusInfo?.phase === 'saving_result'; // 回传阶段
  
  // 🆕 新策略：生成阶段 + 回传阶段都显示百分比条
  const isGenerating = statusInfo?.phase === 'generating';
  // saving_result 阶段：从状态文字中提取百分比（如 "正在下载视频 29%"）
  let savingProgress = 0;
  if (isSavingResult && statusText) {
    const match = statusText.match(/(\d+)%/);
    if (match) savingProgress = parseInt(match[1], 10);
  }
  const displayProgress = isGenerating ? Math.max(0, progress || 0) : isSavingResult ? savingProgress : 0;
  const showProgressBar = isGenerating || (isSavingResult && savingProgress > 0);
  

  // transparent 模式（预留）：遮罩半透明，可透出下方封面图。
  // 非 transparent：底色跟随卡片令牌（浅色模式图片=浅蓝/视频=浅红，与空节点一致），
  // 深色模式令牌回退到 --node-bg(#202020)，观感与原来完全一致。
  const bgColor = transparent
    ? 'rgba(0, 0, 0, 0.55)'
    : (isVideo ? 'var(--node-bg-video)' : isAudio ? 'var(--node-bg)' : 'var(--node-bg-image)');

  // 生成时前景（icon/文字/进度条）统一白色，浅色模式也一样，与深色模式观感一致。
  const fg = { strong: 'rgba(255,255,255,0.8)', mid: 'rgba(255,255,255,0.5)', faint: 'rgba(255,255,255,0.4)', icon: 'rgba(255,255,255,0.4)', track: 'rgba(255,255,255,0.15)', bar: 'rgba(255,255,255,0.5)' };

  return (
    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: bgColor, borderRadius: '0px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', zIndex: 100 }}>
      {/* 星云背景动画 — transparent 模式下隐藏 */}
      {!transparent && <NebulaBackground type={type} animated={true} opacity={0.8} />}

      <div style={{ zIndex: 110, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
        {isSavingResult ?
          <div style={{ width: 48, height: 48, border: `2px solid ${fg.track}`, borderTop: `2px solid ${fg.mid}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> :
         isVideo ? <Film size={48} color={fg.icon} style={{ animation: 'spin 3s linear infinite' }} /> :
         isAudio ? <AudioLines size={48} color={fg.icon} style={{ animation: 'spin 3s linear infinite' }} /> :
         <ImageIcon size={48} color={fg.icon} style={{ animation: 'spin 3s linear infinite' }} />}
        
        {/* 状态信息显示区 (Fixed Layout to prevent jumping) */}
        <div style={{ 
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', marginTop: '4px',
            minHeight: '60px', // Increased height to accommodate larger model name
            justifyContent: 'flex-start'
        }}>
          {/* Model Name & Logo Display */}
          {(modelName || modelLogo) && (
             <div style={{
                height: '24px', lineHeight: '24px',
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '16px', color: fg.strong, fontWeight: 'bold', letterSpacing: '0.5px',
                textAlign: 'center', maxWidth: '220px'
             }}>
               {/* Show Logo if available and no error, else Box icon */}
               {modelLogo && !imgError ? (
                 <img 
                    src={modelLogo} 
                    alt="" 
                    style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 3 }} 
                    onError={() => setImgError(true)} 
                 />
               ) : (
                 <Box size={18} style={{ opacity: 0.5 }} /> 
               )}
               <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>
                 {modelName || "测试模型"}
               </div>
             </div>
           )}

          {/* Status Line: Fixed Height, Truncated */}
          <div style={{ 
              height: '14px', lineHeight: '14px', 
              fontSize: '11px', color: fg.faint, fontWeight: 'bold', letterSpacing: '0.5px',
              textAlign: 'center',
              width: '180px', // Wider to fit longer status text
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' // Truncate with ...
          }} title={statusText}>
            {statusText || ''}
          </div>

          {/* Time & Retry Line: Fixed Height */}
          <div style={{ 
              height: '14px', lineHeight: '14px',
              display: 'flex', gap: '8px', fontSize: '11px', color: fg.faint, fontWeight: 'bold', letterSpacing: '0.5px',
              justifyContent: 'center'
          }}>
            {elapsedText ? <span>{elapsedText}</span> : <span style={{opacity:0}}>0s</span>}
            {retryText && <span style={{ color: '#fbbf24' }}>⟳ {retryText}</span>}
          </div>

          {/* Progress Bar — 生成阶段显示，起始 0% 按轮询增长 */}
          {showProgressBar && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '180px', marginTop: '2px' }}>
              <div style={{ flex: 1, height: '1.5px', background: fg.track, borderRadius: '1px', overflow: 'hidden' }}>
                <div style={{ width: `${displayProgress}%`, height: '100%', background: fg.bar, borderRadius: '1px', transition: 'width 0.5s ease' }} />
              </div>
              <span style={{ fontSize: '10px', color: fg.mid, fontWeight: 'bold', minWidth: '28px', textAlign: 'right' }}>{displayProgress}%</span>
            </div>
          )}
        </div>
      </div>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes bounce-down { 0%, 100% { transform: translateY(-4px); } 50% { transform: translateY(4px); } }
      `}</style>
    </div>
  );
};

export const ExtraCard = ({ url, idx, total, size, onSetMain, onDetach, onRemove, isVideo }) => {
  const gap = 24; const w = size.width + gap; const h = size.height + gap;
  // 副图布局：通用网格，支持任意数量（4/5/9…）。
  // 主图占原点(0,0)由节点本体渲染，副图按 ceil(√total) 列数向右、向上铺开。
  // 卡片全局序号 = idx+1（主图为 0），row 向上为负 y、col 向右为正 x。
  const cols = Math.max(1, Math.ceil(Math.sqrt(total)));
  const cardIndex = idx + 1;
  const pos = { x: (cardIndex % cols) * w, y: -Math.floor(cardIndex / cols) * h };
  const [imgRes, setImgRes] = useState(null);

  // 🛡️ 解析 local-cache:// URL → blob URL（浏览器无法直接加载 local-cache:// 协议）。
  //    典型场景：图组里某张图刚压缩/降级、云上传尚未完成时 output 仍是 local-cache://，
  //    若它落到副图位（被设为主图交换、或上传中刷新），ExtraCard 直接渲染会断链。
  //    http(s)/blob/data URL 原样使用，无需解析。
  const [resolvedUrl, setResolvedUrl] = useState(
    url && url.startsWith('local-cache://') ? null : url
  );
  useEffect(() => {
    let cancelled = false;
    if (!url) { setResolvedUrl(null); return; }
    if (!url.startsWith('local-cache://')) { setResolvedUrl(url); return; }
    // local-cache:// URL 自带 crewId/typeFolder/fileName，按文件名查 IndexedDB/内存缓存即可
    setResolvedUrl(null);
    localCacheService.getLocalUrlOrFetch(url, null, null, isVideo ? 'video' : 'image')
      .then((res) => { if (!cancelled && res?.url) setResolvedUrl(res.url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [url, isVideo]);

  // 副图体积（只读显示，不提供压缩入口——压缩仅对主图开放，避免误操作副图）
  const extraBytes = useFileBytes(resolvedUrl);

  const cardStyle = { position: 'absolute', top: 0, left: 0, borderRadius: '14px', border: '0.5px solid #444', background: '#222222', overflow: 'hidden', zIndex: 50, transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' };
  const overlayStyle = { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', opacity: 0, transition: '0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const circleStyle = { width: '18px', height: '18px', borderRadius: '4px', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', border: '0.5px solid rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', transition: 'all 0.2s' };

  return (
    <div className="nodrag" style={{ ...cardStyle, width: size.width, height: size.height, transform: `translate(${pos.x}px, ${pos.y}px)` }}>
      {resolvedUrl ? (
        isVideo
          ? <video src={resolvedUrl} style={{...styles.mediaFull, borderRadius:0}} muted loop onMouseEnter={(e) => e.target.play()} onMouseLeave={(e) => e.target.pause()} />
          : <img src={resolvedUrl} alt="" style={{...styles.mediaFull, borderRadius:0}} onLoad={(e) => setImgRes({ w: e.target.naturalWidth, h: e.target.naturalHeight })} />
      ) : (
        // 解析中（上传未完成、缓存未命中）：显示占位，避免浏览器对 local-cache:// 报断链
        <div style={{ ...styles.mediaFull, borderRadius: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#222', color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
          上传中…
        </div>
      )}
      {imgRes && <NodeResolutionTag w={imgRes.w} h={imgRes.h} fileBytes={extraBytes} />}
      <div className="extra-overlay" style={{ ...overlayStyle, opacity: 1, pointerEvents: 'auto' }}>
        <div style={{ display: 'flex', gap: '4px', position: 'absolute', top: '6px', right: '6px' }}>
          <div style={circleStyle} onClick={() => onSetMain(idx + 1)} title="设为主图"><Check size={11} /></div>
          <div style={circleStyle} onClick={() => onDetach(url)} title="分离"><ExternalLink size={11} /></div>
          {onRemove && <div style={circleStyle} onClick={() => onRemove(url)} title="删除"><X size={11} /></div>}
        </div>
      </div>
    </div>
  );
};